import { DEFAULT_MATERIAL, IMPACT_TUNING, MATERIALS } from '@/content/materials';
import type { ImpactTuning, MaterialId, MaterialSpec } from '@/content/materials';

export interface ImpactEvent {
  readonly speed: number;
  readonly reducedMass: number;
  readonly material: MaterialId | string;
  readonly grazing: number;
  readonly emitter?: string;
  readonly pan?: number;
}

export interface VoiceRequest {
  readonly materialId: MaterialId;
  readonly energy: number;
  readonly speed: number;
  readonly pan: number;
  readonly count: number;
}

export interface LayerGains {
  readonly transient: number;
  readonly body: number;
  readonly sub: number;
  readonly debris: number;
}

export interface VoiceVariation {
  readonly gain: number;
  readonly bankRatio: number;
  readonly modeRatios: readonly number[];
  readonly modeGains: readonly number[];
  readonly brightness: number;
}

export interface VoiceSnapshot {
  readonly loudness: number;
  readonly startedAt: number;
  readonly decay: number;
}

export interface ImpactAudioOptions {
  readonly tuning?: ImpactTuning;
  readonly random?: () => number;
  readonly createContext?: () => AudioContext | null;
}

export function clamp(value: number, low: number, high: number): number {
  if (!Number.isFinite(value)) return low;
  return value < low ? low : value > high ? high : value;
}

export function clamp01(value: number): number {
  return clamp(value, 0, 1);
}

export function dbToGain(db: number): number {
  return Math.pow(10, db / 20);
}

export function centsToRatio(cents: number): number {
  return Math.pow(2, cents / 1200);
}

export function resolveMaterial(id: MaterialId | string): MaterialSpec {
  const found = MATERIALS[id as MaterialId];
  return found ?? MATERIALS[DEFAULT_MATERIAL];
}

export function grazingFromContact(vx: number, vy: number, nx: number, ny: number): number {
  const vLen = Math.hypot(vx, vy);
  const nLen = Math.hypot(nx, ny);
  if (vLen <= 1e-6 || nLen <= 1e-6) return 1;
  const cosine = Math.abs((vx * nx + vy * ny) / (vLen * nLen));
  return clamp01(1 - cosine);
}

export function grazingScale(grazing: number, tuning: ImpactTuning = IMPACT_TUNING): number {
  const g = clamp01(grazing);
  const floor = clamp01(tuning.grazingFloor);
  return floor + (1 - floor) * Math.pow(1 - g, tuning.grazingExponent);
}

export function impactMomentum(speed: number, reducedMass: number): number {
  const v = Math.max(0, Number.isFinite(speed) ? speed : 0);
  const m = Math.max(0, Number.isFinite(reducedMass) ? reducedMass : 0);
  return v * m;
}

export function normalisedEnergy(event: ImpactEvent, tuning: ImpactTuning = IMPACT_TUNING): number {
  const speed = Number.isFinite(event.speed) ? event.speed : 0;
  if (speed < tuning.minSpeed) return 0;
  const momentum = impactMomentum(speed, event.reducedMass) * grazingScale(event.grazing, tuning);
  return clamp01(momentum / Math.max(1e-6, tuning.refMomentum));
}

export function layerGains(
  r: number,
  tuning: ImpactTuning = IMPACT_TUNING,
  material?: MaterialSpec
): LayerGains {
  const e = clamp01(r);
  const transient = tuning.transientWeight * Math.pow(e, tuning.transientExponent);
  const body = tuning.bodyWeight * Math.pow(e, tuning.bodyExponent);
  const sub = tuning.subWeight * Math.pow(e, tuning.subExponent);
  const debris = tuning.debrisWeight * Math.pow(e, tuning.debrisExponent);
  if (!material) return { transient, body, sub, debris };
  return {
    transient: transient * material.transientAmount,
    body: body * material.bodyAmount,
    sub: sub * material.subAmount,
    debris: debris * material.debrisAmount
  };
}

export function voiceLoudness(
  r: number,
  tuning: ImpactTuning = IMPACT_TUNING,
  material?: MaterialSpec
): number {
  const g = layerGains(r, tuning, material);
  return g.transient + g.body + g.sub + g.debris;
}

export function brightnessHz(
  speed: number,
  material: MaterialSpec,
  tuning: ImpactTuning = IMPACT_TUNING,
  jitter = 1
): number {
  const span = Math.max(1e-6, tuning.refSpeed - tuning.minSpeed);
  const normalised = clamp01((speed - tuning.minSpeed) / span);
  const shaped = Math.pow(normalised, tuning.brightnessExponent);
  const octaves = material.brightnessCeiling / material.brightnessFloor;
  return clamp(material.brightnessFloor * Math.pow(octaves, shaped) * jitter, 40, 18000);
}

export function qFromT60(t60: number, f0: number): number {
  return Math.max(0.0001, (Math.max(0, t60) * Math.max(0, f0)) / 2.2);
}

export function t60FromQ(q: number, f0: number): number {
  return (2.2 * Math.max(0, q)) / Math.max(1e-6, f0);
}

export function materialDecaySeconds(
  material: MaterialSpec,
  tuning: ImpactTuning = IMPACT_TUNING
): number {
  let longest = 0;
  for (const mode of material.modes) {
    const t60 = t60FromQ(mode.q, material.f0 * mode.ratio);
    if (t60 > longest) longest = t60;
  }
  return clamp(longest, 0.02, tuning.maxVoiceSeconds);
}

export function coalesceRequests(
  requests: readonly VoiceRequest[],
  limit: number = IMPACT_TUNING.coalesceLimit
): VoiceRequest[] {
  if (requests.length === 0) return [];
  if (requests.length <= limit) return requests.slice();

  const byMaterial = new Map<MaterialId, VoiceRequest>();
  for (const request of requests) {
    const existing = byMaterial.get(request.materialId);
    byMaterial.set(request.materialId, existing ? mergeRequests(existing, request) : request);
  }

  const merged = Array.from(byMaterial.values());
  if (merged.length <= limit) return merged;

  merged.sort((a, b) => b.energy - a.energy);
  const kept = merged.slice(0, Math.max(1, limit - 1));
  let rest = merged[Math.max(1, limit - 1)];
  for (let i = Math.max(1, limit - 1) + 1; i < merged.length; i++) {
    rest = mergeRequests(rest, merged[i]);
  }
  kept.push(rest);
  return kept;
}

export function mergeRequests(a: VoiceRequest, b: VoiceRequest): VoiceRequest {
  const count = a.count + b.count;
  const weightA = a.count / Math.max(1, count);
  return {
    materialId: a.energy >= b.energy ? a.materialId : b.materialId,
    energy: clamp01(a.energy + b.energy),
    speed: Math.max(a.speed, b.speed),
    pan: clamp(a.pan * weightA + b.pan * (1 - weightA), -1, 1),
    count
  };
}

export function residualLoudness(voice: VoiceSnapshot, now: number): number {
  const age = Math.max(0, now - voice.startedAt);
  const decay = Math.max(1e-6, voice.decay);
  if (age >= decay) return 0;
  return voice.loudness * Math.pow(10, (-3 * age) / decay);
}

export function selectStealIndex(voices: readonly VoiceSnapshot[], now: number): number {
  if (voices.length === 0) return -1;
  let index = 0;
  let quietest = Number.POSITIVE_INFINITY;
  for (let i = 0; i < voices.length; i++) {
    const level = residualLoudness(voices[i], now);
    if (level < quietest) {
      quietest = level;
      index = i;
    }
  }
  return index;
}

export function makeVariation(
  modeCount: number,
  random: () => number,
  tuning: ImpactTuning = IMPACT_TUNING
): VoiceVariation {
  const modeRatios: number[] = [];
  const modeGains: number[] = [];
  for (let i = 0; i < modeCount; i++) {
    modeRatios.push(centsToRatio(bipolar(random) * tuning.modeCents));
    modeGains.push(dbToGain(bipolar(random) * tuning.modeGainDb));
  }
  return {
    gain: dbToGain(bipolar(random) * tuning.gainJitterDb),
    bankRatio: centsToRatio(bipolar(random) * tuning.bankCents),
    modeRatios,
    modeGains,
    brightness: 1 + bipolar(random) * tuning.brightnessJitter
  };
}

export function debrisOffsets(
  count: number,
  spread: number,
  minGap: number,
  random: () => number
): number[] {
  const total = Math.max(0, Math.floor(count));
  if (total === 0) return [];
  const raw: number[] = [];
  for (let i = 0; i < total; i++) raw.push(random() * spread);
  raw.sort((a, b) => a - b);
  const spaced: number[] = [];
  let previous = -minGap;
  for (const value of raw) {
    const placed = Math.max(value, previous + minGap);
    spaced.push(placed);
    previous = placed;
  }
  return spaced;
}

export function makeSoftClipCurve(length: number, drive: number) {
  const size = Math.max(4, Math.floor(length));
  const curve = new Float32Array(size);
  const k = Math.max(0.05, drive);
  const norm = Math.tanh(k);
  for (let i = 0; i < size; i++) {
    const x = (i / (size - 1)) * 2 - 1;
    curve[i] = Math.tanh(k * x) / norm;
  }
  return curve;
}

export function softClipUnityGain(drive: number): number {
  const k = Math.max(0.05, drive);
  return k / Math.tanh(k);
}

export function bipolar(random: () => number): number {
  return random() * 2 - 1;
}

export class CooldownGate {
  private readonly last = new Map<string, number>();

  constructor(private readonly cooldownSeconds: number = IMPACT_TUNING.cooldownSeconds) {}

  allow(emitter: string, material: string, now: number): boolean {
    const key = `${emitter}|${material}`;
    const previous = this.last.get(key);
    if (previous !== undefined && now - previous < this.cooldownSeconds) return false;
    if (this.last.size > 256) this.prune(now);
    this.last.set(key, now);
    return true;
  }

  clear(): void {
    this.last.clear();
  }

  get size(): number {
    return this.last.size;
  }

  private prune(now: number): void {
    for (const [key, at] of this.last) {
      if (now - at >= this.cooldownSeconds) this.last.delete(key);
    }
  }
}

interface Voice {
  readonly gain: GainNode;
  readonly sources: AudioScheduledSourceNode[];
  readonly chain: AudioNode[];
  readonly startedAt: number;
  readonly loudness: number;
  readonly decay: number;
  timer: ReturnType<typeof setTimeout> | null;
}

function excite(param: AudioParam, at: number, attack: number, release: number, peak: number): void {
  param.setValueAtTime(0, at);
  param.linearRampToValueAtTime(peak, at + attack);
  param.linearRampToValueAtTime(0, at + attack + release);
}

function fadeOut(param: AudioParam, at: number, seconds: number): void {
  const current = Math.max(0.0001, param.value);
  param.cancelScheduledValues(at);
  param.setValueAtTime(current, at);
  param.exponentialRampToValueAtTime(0.0001, at + seconds);
  param.setValueAtTime(0, at + seconds + 0.001);
}

function defaultContext(): AudioContext | null {
  if (typeof AudioContext === 'undefined') return null;
  return new AudioContext({ latencyHint: 'interactive' });
}

export class ImpactAudio {
  private ctx: AudioContext | null;
  private master: GainNode | null = null;
  private clipper: WaveShaperNode | null = null;
  private noise: AudioBuffer | null = null;
  private readonly voices: Voice[] = [];
  private readonly pending: VoiceRequest[] = [];
  private flushQueued = false;
  private disposed = false;
  private masterLevel: number;
  private readonly tuning: ImpactTuning;
  private readonly random: () => number;
  private readonly createContext: () => AudioContext | null;
  private readonly gate: CooldownGate;

  constructor(context?: AudioContext | null, options: ImpactAudioOptions = {}) {
    this.ctx = context ?? null;
    this.tuning = options.tuning ?? IMPACT_TUNING;
    this.random = options.random ?? Math.random;
    this.createContext = options.createContext ?? defaultContext;
    this.masterLevel = this.tuning.masterGain;
    this.gate = new CooldownGate(this.tuning.cooldownSeconds);
    if (this.ctx) this.build();
  }

  get context(): AudioContext | null {
    return this.ctx;
  }

  get running(): boolean {
    return this.ctx !== null && this.ctx.state === 'running';
  }

  get voiceCount(): number {
    return this.voices.length;
  }

  get now(): number {
    return this.ctx ? this.ctx.currentTime : 0;
  }

  async resume(): Promise<boolean> {
    if (this.disposed) return false;
    if (!this.ctx) {
      const created = this.createContext();
      if (!created) return false;
      this.ctx = created;
      this.build();
    }
    if (this.ctx.state !== 'running') {
      try {
        await this.ctx.resume();
      } catch {
        return false;
      }
    }
    return this.ctx.state === 'running';
  }

  async suspend(): Promise<void> {
    if (!this.ctx || this.ctx.state !== 'running') return;
    try {
      await this.ctx.suspend();
    } catch {
      return;
    }
  }

  setMasterGain(level: number): void {
    this.masterLevel = clamp(level, 0, 4);
    if (this.master) {
      const at = this.now;
      this.master.gain.setValueAtTime(this.master.gain.value, at);
      this.master.gain.linearRampToValueAtTime(this.busLevel(), at + 0.02);
    }
  }

  private busLevel(): number {
    return this.masterLevel / softClipUnityGain(this.tuning.softClipDrive);
  }

  play(event: ImpactEvent): boolean {
    if (this.disposed) return false;
    const energy = normalisedEnergy(event, this.tuning);
    if (energy <= 0) return false;
    const materialId = resolveMaterial(event.material).id;
    if (!this.gate.allow(event.emitter ?? 'world', materialId, this.now)) return false;
    this.pending.push({
      materialId,
      energy,
      speed: Math.max(0, event.speed),
      pan: clamp(event.pan ?? 0, -1, 1),
      count: 1
    });
    if (!this.flushQueued) {
      this.flushQueued = true;
      queueMicrotask(() => {
        this.flushQueued = false;
        this.flush();
      });
    }
    return true;
  }

  flush(): void {
    if (this.pending.length === 0) return;
    const batch = coalesceRequests(this.pending, this.tuning.coalesceLimit);
    this.pending.length = 0;
    if (!this.ctx || !this.master || !this.noise) return;
    const at = this.ctx.currentTime + this.tuning.lookaheadSeconds;
    for (const request of batch) this.spawn(request, at);
  }

  dispose(): void {
    this.disposed = true;
    this.pending.length = 0;
    for (const voice of this.voices.slice()) this.retire(voice);
    this.voices.length = 0;
    this.gate.clear();
    if (this.master) this.master.disconnect();
    if (this.clipper) this.clipper.disconnect();
    this.master = null;
    this.clipper = null;
    this.noise = null;
  }

  private build(): void {
    const ctx = this.ctx;
    if (!ctx || this.master) return;
    const master = ctx.createGain();
    master.gain.setValueAtTime(this.busLevel(), ctx.currentTime);
    const clipper = ctx.createWaveShaper();
    clipper.curve = makeSoftClipCurve(this.tuning.softClipResolution, this.tuning.softClipDrive);
    clipper.oversample = '2x';
    master.connect(clipper);
    clipper.connect(ctx.destination);
    this.master = master;
    this.clipper = clipper;
    this.noise = this.buildNoise(ctx);
  }

  private buildNoise(ctx: AudioContext): AudioBuffer {
    const frames = Math.floor(ctx.sampleRate * this.tuning.noiseSeconds);
    const buffer = ctx.createBuffer(1, frames, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < frames; i++) data[i] = this.random() * 2 - 1;
    return buffer;
  }

  private snapshots(): VoiceSnapshot[] {
    return this.voices.map((voice) => ({
      loudness: voice.loudness,
      startedAt: voice.startedAt,
      decay: voice.decay
    }));
  }

  private spawn(request: VoiceRequest, at: number): void {
    const ctx = this.ctx;
    const master = this.master;
    const noise = this.noise;
    if (!ctx || !master || !noise) return;

    if (this.voices.length >= this.tuning.maxVoices) {
      const index = selectStealIndex(this.snapshots(), ctx.currentTime);
      if (index >= 0) this.steal(this.voices[index], ctx.currentTime);
    }

    const material = resolveMaterial(request.materialId);
    const gains = layerGains(request.energy, this.tuning, material);
    const variation = makeVariation(material.modes.length, this.random, this.tuning);
    const decay = materialDecaySeconds(material, this.tuning);
    const sources: AudioScheduledSourceNode[] = [];

    const voiceGain = ctx.createGain();
    voiceGain.gain.setValueAtTime(clamp(variation.gain, 0, 4), at);

    const lowpass = ctx.createBiquadFilter();
    lowpass.type = 'lowpass';
    lowpass.Q.value = 0;
    const cutoff = brightnessHz(request.speed, material, this.tuning, variation.brightness);
    lowpass.frequency.setValueAtTime(cutoff, at);
    lowpass.frequency.exponentialRampToValueAtTime(
      Math.max(60, cutoff * this.tuning.brightnessDecay),
      at + this.tuning.brightnessDecaySeconds
    );

    const chain: AudioNode[] = [voiceGain, lowpass];
    voiceGain.connect(lowpass);
    let tail: AudioNode = lowpass;
    if (Math.abs(request.pan) > 0.02 && typeof ctx.createStereoPanner === 'function') {
      const panner = ctx.createStereoPanner();
      panner.pan.setValueAtTime(request.pan, at);
      lowpass.connect(panner);
      chain.push(panner);
      tail = panner;
    }
    tail.connect(master);

    const window = this.tuning.excitationAttack + this.tuning.excitationRelease;
    const excitation = ctx.createBufferSource();
    excitation.buffer = noise;
    const offset = this.random() * Math.max(0.001, this.tuning.noiseSeconds - 0.25);
    excitation.start(at, offset, window + 0.02);
    sources.push(excitation);

    const tick = ctx.createGain();
    excite(
      tick.gain,
      at,
      this.tuning.excitationAttack,
      this.tuning.excitationRelease,
      gains.transient
    );
    const colour = ctx.createBiquadFilter();
    colour.type = 'highpass';
    colour.Q.value = 0;
    colour.frequency.setValueAtTime(material.transientHz, at);
    excitation.connect(tick);
    tick.connect(colour);
    colour.connect(voiceGain);
    chain.push(tick, colour);

    if (gains.body > this.tuning.layerFloor * 0.25) {
      const drive = ctx.createGain();
      excite(
        drive.gain,
        at,
        this.tuning.excitationAttack,
        this.tuning.excitationRelease,
        gains.body
      );
      excitation.connect(drive);
      chain.push(drive);
      for (let i = 0; i < material.modes.length; i++) {
        const mode = material.modes[i];
        const band = ctx.createBiquadFilter();
        band.type = 'bandpass';
        const frequency = material.f0 * mode.ratio * variation.bankRatio * variation.modeRatios[i];
        band.frequency.setValueAtTime(clamp(frequency, 20, 18000), at);
        band.Q.setValueAtTime(Math.max(0.1, mode.q), at);
        const level = ctx.createGain();
        level.gain.setValueAtTime(mode.gain * variation.modeGains[i], at);
        drive.connect(band);
        band.connect(level);
        level.connect(voiceGain);
        chain.push(band, level);
      }
    }

    if (gains.sub > this.tuning.layerFloor) {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(this.tuning.subStartHz, at);
      osc.frequency.exponentialRampToValueAtTime(
        this.tuning.subEndHz,
        at + this.tuning.subSweepSeconds
      );
      const level = ctx.createGain();
      const end = at + this.tuning.subDecaySeconds;
      level.gain.setValueAtTime(0, at);
      level.gain.linearRampToValueAtTime(gains.sub, at + 0.004);
      level.gain.exponentialRampToValueAtTime(0.0001, end);
      level.gain.setValueAtTime(0, end + 0.001);
      osc.connect(level);
      level.connect(voiceGain);
      osc.start(at);
      osc.stop(end + 0.02);
      sources.push(osc);
      chain.push(level);
    }

    if (gains.debris > this.tuning.layerFloor) {
      const grains = Math.round(material.debrisGrains * request.energy);
      const offsets = debrisOffsets(
        grains,
        this.tuning.debrisSpreadSeconds,
        this.tuning.debrisGrainSeconds * 1.5,
        this.random
      );
      if (offsets.length > 0) {
        const scatter = ctx.createBufferSource();
        scatter.buffer = noise;
        const level = ctx.createGain();
        level.gain.setValueAtTime(0, at);
        const half = this.tuning.debrisGrainSeconds * 0.5;
        let last = at;
        for (const grainOffset of offsets) {
          const grainAt = at + 0.008 + grainOffset;
          const peak = gains.debris * (0.4 + this.random() * 0.6);
          excite(level.gain, grainAt, half, half, peak);
          last = grainAt + this.tuning.debrisGrainSeconds;
        }
        scatter.connect(level);
        level.connect(voiceGain);
        scatter.start(at, this.random() * Math.max(0.001, this.tuning.noiseSeconds - 0.6));
        scatter.stop(last + 0.02);
        sources.push(scatter);
        chain.push(level);
      }
    }

    const voice: Voice = {
      gain: voiceGain,
      sources,
      chain,
      startedAt: at,
      loudness: voiceLoudness(request.energy, this.tuning, material) * variation.gain,
      decay,
      timer: null
    };
    this.voices.push(voice);
    const lifetime = Math.max(0.05, at + decay + 0.1 - ctx.currentTime);
    voice.timer = setTimeout(() => this.retire(voice), lifetime * 1000);
  }

  private steal(voice: Voice, now: number): void {
    fadeOut(voice.gain.gain, now, 0.01);
    for (const source of voice.sources) {
      try {
        source.stop(now + 0.02);
      } catch {
        continue;
      }
    }
    const index = this.voices.indexOf(voice);
    if (index >= 0) this.voices.splice(index, 1);
    if (voice.timer !== null) clearTimeout(voice.timer);
    voice.timer = setTimeout(() => this.disconnect(voice), 80);
  }

  private retire(voice: Voice): void {
    const index = this.voices.indexOf(voice);
    if (index >= 0) this.voices.splice(index, 1);
    if (voice.timer !== null) {
      clearTimeout(voice.timer);
      voice.timer = null;
    }
    this.disconnect(voice);
  }

  private disconnect(voice: Voice): void {
    for (const source of voice.sources) {
      try {
        source.disconnect();
      } catch {
        continue;
      }
    }
    for (const node of voice.chain) {
      try {
        node.disconnect();
      } catch {
        continue;
      }
    }
  }
}

export function createImpactAudio(
  context?: AudioContext | null,
  options: ImpactAudioOptions = {}
): ImpactAudio {
  return new ImpactAudio(context, options);
}
